import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Input } from '@/components/ui/input';
import { MapPin, Loader2 } from 'lucide-react';
import { useGoogleMapsScript } from '@/hooks/useGoogleMapsScript';

/**
 * Parse a Google Place ``address_components`` array into the four flat
 * fields our lead form uses. Falls back gracefully when a component is
 * missing (e.g. rural addresses without zip).
 */
export function parseAddressComponents(components) {
  const out = { address: '', city: '', state: '', zip_code: '' };
  if (!Array.isArray(components)) return out;
  let streetNumber = '';
  let route = '';
  let sublocality = '';
  for (const c of components) {
    const types = c.types || [];
    if (types.includes('street_number')) streetNumber = c.long_name;
    else if (types.includes('route')) route = c.long_name;
    else if (types.includes('locality')) out.city = c.long_name;
    else if (types.includes('sublocality') || types.includes('sublocality_level_1')) sublocality = c.long_name;
    else if (types.includes('administrative_area_level_1')) out.state = c.short_name || c.long_name;
    else if (types.includes('postal_code')) out.zip_code = c.long_name;
  }
  out.address = [streetNumber, route].filter(Boolean).join(' ').trim();
  if (!out.city) out.city = sublocality;  // small towns sometimes only have sublocality
  return out;
}

/**
 * Shadcn-styled address input with Google Places autocomplete. Uses the
 * stable JS ``AutocompleteService`` + ``PlacesService`` APIs so we keep
 * full control of the React-controlled Input and the suggestions dropdown.
 * Keyboard accessible (↑ ↓ Enter Esc).
 *
 * Props:
 *   value         — current street-address string
 *   onChange      — called on every keystroke with the new string
 *   onPlaceSelect — called with parsed ``{address, city, state, zip_code}``
 *                   when the user picks a suggestion
 *   placeholder   — Input placeholder
 *   testid        — data-testid for the underlying Input
 */
export function AddressAutocomplete({ value, onChange, onPlaceSelect, placeholder, testid, types = ['address'] }) {
  const { ready, error } = useGoogleMapsScript();
  const [predictions, setPredictions] = useState([]);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const inputRef = useRef(null);
  const sessionTokenRef = useRef(null);
  const autocompleteServiceRef = useRef(null);
  const placesServiceRef = useRef(null);
  const debounceRef = useRef(null);

  // Lazy-init Google services as soon as the script is ready.
  useEffect(() => {
    if (!ready || !window.google?.maps?.places?.AutocompleteService) return;
    autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
    placesServiceRef.current = new window.google.maps.places.PlacesService(document.createElement('div'));
    sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
  }, [ready]);

  const fetchPredictions = useCallback((input) => {
    if (!autocompleteServiceRef.current || !input || input.length < 3) {
      setPredictions([]);
      return;
    }
    autocompleteServiceRef.current.getPlacePredictions(
      {
        input,
        sessionToken: sessionTokenRef.current,
        types,
      },
      (preds, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && Array.isArray(preds)) {
          setPredictions(preds.slice(0, 6));
          setHighlight(-1);
        } else {
          setPredictions([]);
        }
      },
    );
  }, [types]);

  const handleChange = (e) => {
    const v = e.target.value;
    onChange?.(v);
    if (!ready) return;
    setOpen(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchPredictions(v), 180);
  };

  const handlePick = useCallback((p) => {
    if (!p || !placesServiceRef.current) return;
    setLoadingDetails(true);
    placesServiceRef.current.getDetails(
      {
        placeId: p.place_id,
        fields: ['address_components', 'formatted_address'],
        sessionToken: sessionTokenRef.current,
      },
      (place, status) => {
        setLoadingDetails(false);
        if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
          const parsed = parseAddressComponents(place.address_components);
          onPlaceSelect?.(parsed);
          setOpen(false);
          setPredictions([]);
          // A new session token must be used per autocomplete session (billing).
          sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
        }
      },
    );
  }, [onPlaceSelect]);

  const handleKeyDown = (e) => {
    if (!open || predictions.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight(h => Math.min(h + 1, predictions.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight(h => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter' && highlight >= 0) {
      e.preventDefault();
      handlePick(predictions[highlight]);
    } else if (e.key === 'Escape') { setOpen(false); }
  };

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (!inputRef.current) return;
      if (!inputRef.current.contains(e.target) && !e.target.closest?.('[data-autocomplete-list]')) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const showDropdown = open && (predictions.length > 0 || loadingDetails);
  const showError = useMemo(() => Boolean(error && !ready && process.env.NODE_ENV === 'development'), [error, ready]);

  return (
    <div className="relative" ref={inputRef}>
      <div className="relative">
        <Input
          value={value || ''}
          onChange={handleChange}
          onFocus={() => { if (predictions.length > 0) setOpen(true); }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder || '123 Main St'}
          className="mt-1 rounded-sm pr-8"
          data-testid={testid || 'address-autocomplete-input'}
          autoComplete="off"
        />
        {loadingDetails ? (
          <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 mt-0.5 w-3.5 h-3.5 text-slate-400 animate-spin" />
        ) : (
          <MapPin className="absolute right-2 top-1/2 -translate-y-1/2 mt-0.5 w-3.5 h-3.5 text-slate-400" />
        )}
      </div>
      {showDropdown && (
        <ul
          className="absolute z-50 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-sm shadow-lg max-h-64 overflow-y-auto"
          data-autocomplete-list
          data-testid="address-autocomplete-list"
        >
          {predictions.map((p, i) => (
            <li key={p.place_id}>
              <button
                type="button"
                onClick={() => handlePick(p)}
                onMouseEnter={() => setHighlight(i)}
                className={`w-full text-left px-3 py-2 flex items-start gap-2 ${highlight === i ? 'bg-blue-50' : 'hover:bg-slate-50'}`}
                data-testid={`address-suggestion-${i}`}
              >
                <MapPin className="w-3.5 h-3.5 text-[#0047AB] mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <p className="text-xs font-medium text-slate-900 truncate">
                    {p.structured_formatting?.main_text || p.description}
                  </p>
                  <p className="text-[11px] text-slate-500 truncate">
                    {p.structured_formatting?.secondary_text || ''}
                  </p>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
      {showError && (
        <p className="text-[10px] text-amber-600 mt-1" data-testid="address-autocomplete-error">
          Address autocomplete unavailable: {error}
        </p>
      )}
    </div>
  );
}
